import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EVENT_TYPES = ["Birthday Party", "Girls' Night", "Bridal Shower", "Baby Shower", "Church Event", "Women's Ministry Event", "Corporate Event", "Private Party", "Special Celebration", "Other"];
const EMPTY = { name: "", email: "", phone: "", event_date: "", event_location: "", event_type: "", guests: "", preferred_time: "", details: "" };

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.28em] text-stone2">{label}</span>
    {children}
  </label>
);

const STEPS = [
  ["01", "Choose Your Fragrances", "Browse a curated fragrance library and find the scents that speak to you."],
  ["02", "Blend & Pour", "Learn to blend notes and hand-pour your own soy wax candle, guided step by step."],
  ["03", "Take It Home", "Leave with a beautiful candle you made yourself — and the memory of making it."],
];

const Experience = () => {
  const [form, setForm] = useState(EMPTY);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, { type: "experience", data: form });
      setSent(true);
      toast.success("Your booking inquiry has been sent");
    } catch {
      toast.error("Something went wrong — please try again");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="experience-page" className="bg-ink text-ivory">
      <section className="py-20 md:py-32">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20 grid gap-16 lg:grid-cols-2 items-center">
          <Reveal>
            <p className="eyebrow !text-ivory/50">The Merari Candle Experience</p>
            <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]">
              We Bring the Candle-Making Experience <em>to You</em>
            </h1>
            <p className="mt-8 max-w-lg text-sm md:text-base leading-relaxed text-ivory/70">
              Merari Candle Company offers a mobile candle-making experience where guests learn to create their own soy wax candles — selecting fragrances, blending scents, and pouring something special to take home.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border border-white/15 p-3">
              <img src="/assets/strawberry.png" alt="Merari soy candle experience" className="h-[340px] md:h-[480px] w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
          <Reveal><p className="eyebrow !text-ivory/50">How It Unfolds</p></Reveal>
          <div className="mt-10 grid gap-px bg-white/10 border border-white/10 md:grid-cols-3">
            {STEPS.map(([n, t, d], i) => (
              <Reveal key={n} delay={i * 0.1} className="bg-ink p-10">
                <span className="font-mono text-xs tracking-[0.3em] text-ivory/40">{n}</span>
                <h3 className="mt-4 font-serif text-2xl font-light">{t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-ivory/60">{d}</p>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-14">
            <p className="eyebrow !text-ivory/50">Perfect For</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {EVENT_TYPES.slice(0, 9).map((o) => (
                <span key={o} className="border border-white/15 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-ivory/70">{o}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 bg-ivory py-20 md:py-28 text-ink">
        <div className="mx-auto max-w-3xl px-6 sm:px-12">
          <Reveal className="text-center">
            <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight">Book a Candle Experience</h2>
            <p className="mt-5 text-sm md:text-base text-stone2">Tell us about your event and we'll craft the experience around it.</p>
          </Reveal>
          {sent ? (
            <Reveal className="mt-12 border border-line bg-white p-12 text-center" data-testid="experience-success">
              <p className="font-serif text-3xl font-light italic">We can't wait to pour with you.</p>
              <p className="mt-4 text-sm text-stone2 leading-relaxed">Your booking inquiry has been received. We'll be in touch within 2-3 business days to plan your experience.</p>
              <button onClick={() => { setSent(false); setForm(EMPTY); }} data-testid="experience-book-another" className="btn-outline mt-8">Book Another Event</button>
            </Reveal>
          ) : (
            <Reveal delay={0.1}>
              <form onSubmit={submit} data-testid="experience-booking-form" className="mt-12 border border-line bg-white p-8 md:p-12 grid gap-6 sm:grid-cols-2">
                <Field label="Name" testid="field-exp-name"><input required data-testid="exp-name-input" value={form.name} onChange={set("name")} placeholder="Your full name" /></Field>
                <Field label="Email" testid="field-exp-email"><input required type="email" data-testid="exp-email-input" value={form.email} onChange={set("email")} placeholder="you@email.com" /></Field>
                <Field label="Phone" testid="field-exp-phone"><input data-testid="exp-phone-input" value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" /></Field>
                <Field label="Event Date" testid="field-exp-date"><input required type="date" data-testid="exp-date-input" value={form.event_date} onChange={set("event_date")} /></Field>
                <Field label="Event Location" testid="field-exp-location"><input required data-testid="exp-location-input" value={form.event_location} onChange={set("event_location")} placeholder="City, venue or address" /></Field>
                <Field label="Event Type" testid="field-exp-type">
                  <select required data-testid="exp-type-select" value={form.event_type} onChange={set("event_type")}>
                    <option value="">Select event type…</option>
                    {EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Number of Guests" testid="field-exp-guests"><input required type="number" min="1" data-testid="exp-guests-input" value={form.guests} onChange={set("guests")} placeholder="e.g. 12" /></Field>
                <Field label="Preferred Time" testid="field-exp-time">
                  <select data-testid="exp-time-select" value={form.preferred_time} onChange={set("preferred_time")}>
                    <option value="">Select a time…</option><option>Morning</option><option>Afternoon</option><option>Evening</option>
                  </select>
                </Field>
                <div className="sm:col-span-2"><Field label="Additional Details" testid="field-exp-details"><textarea rows="4" data-testid="exp-details-input" value={form.details} onChange={set("details")} placeholder="Tell us about your celebration…" /></Field></div>
                <div className="sm:col-span-2">
                  <button type="submit" disabled={loading} data-testid="experience-submit" className="btn-primary w-full disabled:opacity-50">
                    {loading ? "Sending…" : "Book a Candle Experience"}
                  </button>
                </div>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
};

export default Experience;
